import {
  faBrain,
  faFolderOpen,
  faKey,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useApiCall from 'contexts/ApiCall';
import AssignmentSubmission from 'models/AssignmentSubmission';
import Module from 'models/Module';
import StudentGradebookEntry from 'models/StudentGradebookEntry';
import React, { useEffect, useState } from 'react';
import { moduleService } from 'services/moduleService';
import ActionButton from 'view/components/buttons/ActionButton';
import { AudioAssistanceButton } from 'view/components/buttons/AudioAssistanceButton';
import { AppPage } from 'view/components/common/AppPage';
import ItemPanel from 'view/components/common/ItemPanel';
import AppSidebar from 'view/components/common/Sidebar';
import { WordList } from 'view/components/common/WordList';

interface ModuleViewProps {
  moduleId: string;
  onSelect?: (module: Module) => void;
  onSelectAssignment?: (assignment: StudentGradebookEntry) => void;
  onExit: () => void;
}

const ModuleView: React.FC<ModuleViewProps> = ({
  moduleId,
  onSelect,
  onSelectAssignment,
  onExit,
}) => {
  const [selectedAssignment, setSelectedAssignment] =
    useState<StudentGradebookEntry>();
  const [assignments, setAssignments] = useState<StudentGradebookEntry[]>();
  const [module, setModule] = useState<Module>();
  const makeApiCall = useApiCall();

  useEffect(() => {
    makeApiCall(moduleService.getModule, moduleId)
      .then((resp) => setModule(resp))
      .catch((err) => alert(err));

    makeApiCall(moduleService.getModuleAssignments, moduleId)
      .then((resp) =>
        setAssignments(
          resp.map((a: Record<string, any>) => {
            return {
              assignment: a.assignment ?? a,
              submission:
                a.submission ?? AssignmentSubmission.generateDefault(a.id),
            };
          }),
        ),
      )
      .catch((err) => alert(err));
  }, [moduleId, makeApiCall]);

  const selectAssignment = (assignment: StudentGradebookEntry) => {
    if (
      assignment.assignment.id.toString() ===
        selectedAssignment?.assignment.id.toString() &&
      onSelectAssignment
    ) {
      // Call the onSelectAssignment prop
      onSelectAssignment(assignment);
    } else {
      setSelectedAssignment(assignment);
    }
  };

  if (!module) return <AppPage />;

  return (
    <div className="app-page-content">
      <AppSidebar onExit={onExit}>
        <div className="sidebar-content-data">
          <div className="row">
            <FontAwesomeIcon icon={faFolderOpen} />
            <label className="label-normal">Module: {module.name}</label>
            <AudioAssistanceButton text={`Module: ${module.name}`} />
          </div>
          <label className="label-small">
            <i>{module.description}</i>
          </label>
        </div>
        {module.key_words && module.key_words.length > 0 && (
          <div className="sidebar-content-data">
            <div className="row">
              <FontAwesomeIcon icon={faKey} />
              <label className="label-normal">Key words:</label>
              <AudioAssistanceButton text="Key words" />
            </div>
            <WordList words={module.key_words} />
          </div>
        )}
        {module.advanced_words && module.advanced_words.length > 0 && (
          <div className="sidebar-content-data">
            <div className="row">
              <FontAwesomeIcon icon={faBrain} />
              <label className="label-normal">Advanced words:</label>
              <AudioAssistanceButton text="Key words" />
            </div>
            <WordList words={module.advanced_words} />
          </div>
        )}
        {onSelect && (
          <ActionButton
            label="Import to classroom"
            onClick={() => onSelect(module)}
          />
        )}
      </AppSidebar>
      <div className="app-main-content">
        <h1> Module: {module.name}</h1>
        <div className="item-list">
          {assignments?.map((a: StudentGradebookEntry) => (
            <ItemPanel
              selected={a === selectedAssignment}
              key={`assignment-${a.assignment.id}`}
              id={`assignment-${a.assignment.id}`}
              onClick={() => selectAssignment(a)}
              onDoubleClick={() => selectAssignment(a)}
            >
              {a.assignment.title}
            </ItemPanel>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ModuleView;
